import { Link } from "react-router-dom";
import { useDocumentMeta } from "../lib/useDocumentMeta";
import { toDocumentMeta, getRouteMeta } from "../route-meta";
import { rolloutMode } from "../rollout";

export default function Science() {
  useDocumentMeta(toDocumentMeta(getRouteMeta("/science", rolloutMode)!));
  return (
    <>
      <Hero />
      <MostlyEasy />
      <Studies />
      <section className="mk-container py-12">
        <div className="max-w-3xl">
          <p className="mk-eyebrow">Science-based adaptive training</p>
          <h2 className="mk-h2 mt-3">Research guides the choices. Your runs guide the adjustments.</h2>
          <p className="mt-5 text-px-text-soft">Research informs the amount of harder running, the progression of long runs and the recovery between sessions. PX7 Training applies those findings to your goal, recent training and available time. After a run, what you completed and how it felt help determine whether the next sessions need different durations, intensities or recovery.</p>
          <p className="mt-4 text-px-text-soft">We keep reviewing new research and use runners' training, results and feedback to improve the method and the app. We look at progress, completion and whether sessions fit the time runners have.</p>
          <p className="mt-4 text-px-text-soft">Our articles explain individual studies, who took part, what was measured and how we use the findings.</p>
        </div>
      </section>
      <Sources />
      <CTA />
    </>
  );
}

function Hero() {
  return (
    <section className="mk-container pt-12 sm:pt-20 pb-6">
      <p className="mk-eyebrow">The research</p>
      <h1 className="mk-h1 mt-4 max-w-3xl">The research behind PX7 Training.</h1>
      <p className="mk-lead mt-5 max-w-3xl">
        Research helps us choose relevant sessions and decide when a plan may need to change.
        Here are some of the studies that inform your sessions, recovery and progression.
      </p>
    </section>
  );
}

function MostlyEasy() {
  return (
    <section className="mk-container py-10">
      <div className="max-w-3xl">
        <p className="mk-eyebrow">The starting point</p>
        <h2 className="mk-h2 mt-3">Most of your running should be easy.</h2>
        <p className="mt-5 text-px-text-soft text-[17px] leading-relaxed">
          When researchers look at how well-trained endurance athletes train, they keep finding
          the same thing: most sessions are easy. In Stephen Seiler's studies, it was around 80
          percent. That is where the popular 80/20 idea comes from.
        </p>
        <p className="mt-4 text-px-text-soft text-[17px] leading-relaxed">
          A 2025 analysis of 13 studies found no significant overall difference between polarised
          and pyramidal training in improvements to maximal oxygen uptake or time-trial performance.
          The findings suggest that training level matters when choosing the balance of easy and harder running.
        </p>
        <p className="mt-4 text-px-text-soft text-[17px] leading-relaxed">
          PX7 Training keeps most running easy and adjusts the amount and timing of harder
          sessions to your goal, recent training, available time and recovery. After each run,
          it uses what you completed and how it felt to adjust the next sessions. A missed run
          or tired legs can mean a shorter workout, an easy run or more recovery before the
          next hard session.
        </p>
      </div>
    </section>
  );
}

function Studies() {
  const studies = [
    {
      h: "Long runs that grow too fast",
      b: "A Danish-led study of 5,205 runners found more overuse injuries when a run was over 10 percent longer than the longest run in the previous month.",
      to: "/articles/how-long-can-your-long-run-be",
    },
    {
      h: "Training by how recovered you are",
      b: "A small study found larger 10K improvements with training adjusted using recovery and performance signals. The programme used recovery and performance signals together.",
      to: "/articles/train-by-recovery",
    },
    {
      h: "Tapering before a marathon",
      b: "In more than 158,000 marathon runners, a steady three-week taper was linked to finishing about 2.6 percent faster.",
      to: "/articles/marathon-taper",
    },
    {
      h: "Holding up late in a race",
      b: "An analysis of 82,303 marathon runners linked changes in heart rate relative to speed with race performance. The article explains how those changes can help you review a long run.",
      to: "/articles/heart-rate-drift",
    },
  ];
  return (
    <section className="mk-container py-16">
      <p className="mk-eyebrow">Selected studies</p>
      <h2 className="mk-h2 mt-3">Studies on training and recovery.</h2>
      <div className="mt-8 grid gap-4 md:grid-cols-2">
        {studies.map((s) => (
          <Link key={s.to} to={s.to} className="mk-card block hover:border-px-green-dark/40 transition">
            <h3 className="mk-h3">{s.h}</h3>
            <p className="mt-3 text-sm text-px-text-soft leading-relaxed">{s.b}</p>
            <p className="mt-3 text-sm font-semibold text-px-green-dark">Read the article →</p>
          </Link>
        ))}
      </div>
    </section>
  );
}

function Sources() {
  const refs = [
    {
      a: "Sylta, Tønnessen & Seiler (2014)",
      t: "From heart-rate data to training quantification: a comparison of 3 methods of training-intensity analysis. International Journal of Sports Physiology and Performance.",
      href: "https://doi.org/10.1123/ijspp.2013-0298",
    },
    {
      a: "Frandsen et al. (2025)",
      t: "How much running is too much? British Journal of Sports Medicine.",
      href: "https://doi.org/10.1136/bjsports-2024-109380",
    },
    {
      a: "Nuuttila et al. (2022)",
      t: "Individualized endurance training based on recovery and training status in recreational runners. Medicine & Science in Sports & Exercise.",
      href: "https://doi.org/10.1249/MSS.0000000000002968",
    },
    {
      a: "Smyth & Lawlor (2021)",
      t: "Longer disciplined tapers improve marathon performance for recreational runners. Frontiers in Sports and Active Living.",
      href: "https://doi.org/10.3389/fspor.2021.735220",
    },
    {
      a: "Smyth et al. (2022)",
      t: "Decoupling of internal and external workload during a marathon. Sports Medicine.",
      href: "https://doi.org/10.1007/s40279-022-01680-5",
    },
    {
      a: "Seiler (2010)",
      t: "What is best practice for training intensity and duration distribution in endurance athletes? International Journal of Sports Physiology and Performance.",
      href: "https://doi.org/10.1123/ijspp.5.3.276",
    },
    {
      a: "Rosenblat et al. (2025)",
      t: "Which training intensity distribution intervention will produce the greatest improvements? Sports Medicine.",
      href: "https://doi.org/10.1007/s40279-024-02149-3",
    },
  ];
  return (
    <section className="mk-container py-16">
      <p className="mk-eyebrow">Sources</p>
      <h2 className="mk-h2 mt-3">Read the studies yourself.</h2>
      <ul className="mt-6 grid gap-3 md:grid-cols-2 max-w-4xl">
        {refs.map((r) => (
          <li key={r.href} className="mk-card">
            <div className="text-[10px] uppercase tracking-[0.2em] text-px-text-muted">{r.a}</div>
            <a href={r.href} className="block font-semibold mt-1 hover:underline">
              {r.t}
            </a>
          </li>
        ))}
      </ul>
    </section>
  );
}

function CTA() {
  return (
    <section className="mk-container pb-20">
      <div className="rounded-xl2 bg-px-ink text-white p-10 sm:p-14 text-center">
        <p className="mk-eyebrow text-px-green">The method</p>
        <h2 className="mk-h2 text-white mt-3">How PX7 Training chooses a session.</h2>
        <p className="mt-4 text-white/70 max-w-xl mx-auto">
          Read how PX7 Training uses your goal, recent runs and recovery to set the next session.
        </p>
        <div className="mt-7 flex flex-wrap justify-center gap-3">
          <Link to="/method" className="mk-button-ghost border-white/20 bg-white/5 text-white hover:bg-white/10">
            How the method works →
          </Link>
          <Link to="/#newsletter" className="mk-button-accent">Get the newsletter →</Link>

        </div>
      </div>
    </section>
  );
}
